<template>
  <div ref="rootElem" class="v-atlas-raumplanung-features-list">
    <!--#region Search and order panel-->
    <div class="v-atlas-raumplanung-features-list__options">
      <Combobox v-model="searchBoxSelection" :items="props.data?.features" :display-func="(c: any) => c.properties.name"
        :filter-func="(c: any, text: string) => c.properties.name.toLowerCase().includes(text.toLowerCase())"
        placeholder="Finde ...">
        <template #item="{ item }">
          <strong>{{ item?.properties?.name }}</strong>
        </template>
      </Combobox>


      <!--#region Order buttons-->
      <ul class="v-list v-list-chips">
        <li :class="`v-list__item ` + (props.orderBy == 'name' ? 'x-selected' : '')">
          <div>
            <button :data-tooltip="getOrderTooltip('Name')" @click="onOrderButtonClick('name')">
              {{
                getOrderDirArrow('name')
              }}
              Name
            </button>
          </div>
        </li>
        <li :class="`v-list__item ` + (props.orderBy == 'value' ? 'x-selected' : '')">
          <div>
            <button :data-tooltip="getOrderTooltip('Wert')" @click="onOrderButtonClick('value')">
              {{
                getOrderDirArrow('value') }} Wert
            </button>
          </div>
        </li>
      </ul>
      <!--#endregion Order buttons-->
    </div>
    <!--#endregion Search and order panel-->


    <BarChartTable ref="barChartTableRef" v-model:hover="hover" :items="featuresOrdered""
      :value-func="(item) => item.properties.value"
      :label-func="item => item.properties.name" :two-rows-layout="true" :number-format-options="{
        thousandsSeparator: '.',
        decimalSeparator: ',',
        numDecimals: mapModule.metadata?.numDecimals ?? 2
      }" :highlight="searchBoxSelection" @item-click="onFeatureClick" />
  </div>
</template>

  <script setup lang="ts">
  import { Combobox, ContextMenuItem, GeoJsonFeature, GeoJsonFeatureCollection, useSortedArray } from 'gertrud'
  import { onUnmounted, Ref, ref, watch } from "vue";
  import { RaumplanungMapModule } from "@/mapModules/raumplanung/RaumplanungMapModule";
  import BarChartTable from "../../components/BarChartTable.vue";

  import * as uuid from "uuid"
  import { useElementSize } from "@vueuse/core";



  const props = withDefaults(defineProps<{
    data?: GeoJsonFeatureCollection | null
    mapModule: RaumplanungMapModule
    orderBy?: string
    orderDir?: string
  }>(), {
    orderBy: "name",
    orderDir: "asc"
  });

  const barChartTableRef = ref<InstanceType<typeof BarChartTable> | null>(null);

  const searchBoxSelection: Ref<GeoJsonFeature | null> = ref(null)

  const rootElem = ref<HTMLElement | null>(null)

  const emit = defineEmits(["update:order-by", "update:order-dir"])

  const hover: Ref<GeoJsonFeature | null> = ref(null)

  const boundingBox = useElementSize(rootElem as any)

  const contextMenuKey = uuid.v4().toString()


  const featuresOrdered = useSortedArray(
    () => props.data?.features ?? [],
    (item: any) => item?.properties?.[props.orderBy],
    () => props.orderDir == "desc"
  )

  const scrollTo: ContextMenuItem =
  {
    label: "Zum Listeneintrag scrollen",
    action: (feature: any) => {


      // Find the feature in the list and scroll to it:

      // NOTE: We need to perform the search because the feature objects in props.data.features are not the same
      // as the ones we get from the mapModule (e.g. in hover), 
      // even if they represent the same feature on the map.
      for (const f of props.data?.features ?? []) {
        if (f.properties?.id === feature.properties?.id) {
          //    barChartTableRef.value?.scrollToItem(f)
          searchBoxSelection.value = f
          break
        }
      }

    }
  }



  function getOrderTooltip(fieldLabel: string) {
    const dir = props.orderBy === fieldLabel
      ? (props.orderDir === 'asc' ? 'desc' : 'asc')  // same field → toggle
      : props.orderDir;                                 // different field → keep direction
    return dir === 'asc'
      ? `Aufsteigend nach ${fieldLabel} sortieren`
      : `Absteigend nach ${fieldLabel} sortieren`;
  }

  function getOrderDirArrow(field: string) {
    if (props.orderBy === field) {
      if (props.orderDir === 'asc') {
        return '▲';
      } else {
        return '▼';
      }
    } else {
      return '';
    }
  }


  function onOrderButtonClick(field: string) {
    if (field === props.orderBy) {
      // Same button — toggle direction
      const newOrderDir = props.orderDir === 'desc' ? 'asc' : 'desc';
      emit("update:order-dir", newOrderDir)
    }
    // Different button — keep current direction, only change field
    emit("update:order-by", field)
  }


  function onFeatureClick(event: any) {

    props.mapModule.selectedFeatureId = event.item.properties?.id ?? null

  }


  onUnmounted(() => {
    delete props.mapModule.contextMenuEntries[contextMenuKey]
  })

  watch(() => props.mapModule.hoverFeatureId, (newHoverFeatureId) => {

    for (const f of props.data?.features ?? []) {

      if (f.properties?.id === newHoverFeatureId) {
        hover.value = f
        return
      }
    }

  })

  watch(hover, (newHover) => {

    props.mapModule.hoverFeatureId = newHover?.properties?.id ?? null

  })


  watch([boundingBox.width, boundingBox.height], ([w, h]) => {
    if (w == 0 || h == 0) {
      delete props.mapModule.contextMenuEntries[contextMenuKey]
    }
    else {
      props.mapModule.contextMenuEntries[contextMenuKey] = scrollTo
    }
  })


  watch(() => searchBoxSelection.value, (newSelected) => {
    if (newSelected) {

      props.mapModule.hoverFeatureId = newSelected.properties?.id ?? null
      barChartTableRef.value?.scrollToItem(searchBoxSelection.value)

      window.setTimeout(() => {
        searchBoxSelection.value = null
      }, 3000)
    }
  })
</script>